import React from 'react';
import { Plus, X } from 'lucide-react';

export const StageSafety = function({ planData, updateData, setPlanData }) {
  function addRelatedCase() {
    setPlanData(function (prev) {
      return Object.assign({}, prev, {
        relatedCases: (prev.relatedCases || []).concat([{ caseNumber: '', caseType: '' }])
      });
    });
  }
  function updateRelatedCase(idx, field, val) {
    setPlanData(function (prev) {
      var updated = (prev.relatedCases || []).map(function (c, i) {
        return i === idx ? Object.assign({}, c, { [field]: val }) : c;
      });
      return Object.assign({}, prev, { relatedCases: updated });
    });
  }
  function removeRelatedCase(idx) {
    setPlanData(function (prev) {
      return Object.assign({}, prev, {
        relatedCases: (prev.relatedCases || []).filter(function (_, i) { return i !== idx; })
      });
    });
  }

  var relatedCases = planData.relatedCases || [];

  return (
    <div className="space-y-6">
      <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-4 mb-6">
        <p className="text-sm text-white/80">
          <strong className="text-[#EF4444]">Safety Gate:</strong> These questions help determine if special protections are needed.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Is there an active protective order or no-contact order?</label>
        <div className="flex gap-4">
          {['yes', 'no'].map(option => (
            <button
              key={option}
              onClick={() => updateData('protectiveOrder', option)}
              className={`flex-1 p-3 rounded-lg border transition-all ${
                planData.protectiveOrder === option
                  ? 'border-[#EF4444] bg-[#EF4444]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={'protective-order-' + option}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">History of domestic violence?</label>
        <div className="flex gap-4">
          {['yes', 'no'].map(option => (
            <button
              key={option}
              onClick={() => updateData('dvHistory', option)}
              className={`flex-1 p-3 rounded-lg border transition-all ${
                planData.dvHistory === option
                  ? 'border-[#EF4444] bg-[#EF4444]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={'dv-history-' + option}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Supervised visitation required?</label>
        <div className="flex gap-4">
          {['yes', 'no'].map(option => (
            <button
              key={option}
              onClick={() => updateData('supervisedVisitation', option)}
              className={`flex-1 p-3 rounded-lg border transition-all ${
                planData.supervisedVisitation === option
                  ? 'border-[#EF4444] bg-[#EF4444]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={'supervised-visitation-' + option}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Related Cases */}
      <div className="border-t border-white/10 pt-6 space-y-3" data-testid="safety-related-cases-section">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium">Related Case(s) Between the Parties</label>
            <p className="text-xs text-white/40 mt-1">Add any other active or prior cases between the parties (custody, protective order, paternity, etc.).</p>
          </div>
          <button
            onClick={addRelatedCase}
            className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white/50 hover:text-white/70 hover:bg-white/[0.06] transition-colors shrink-0"
            data-testid="add-related-case-btn">
            <Plus className="w-3 h-3" /> Add Case
          </button>
        </div>
        {relatedCases.length === 0 && (
          <p className="text-xs text-white/25 py-2">No related cases added. Click "Add Case" to add one.</p>
        )}
        {relatedCases.map(function (rc, idx) {
          return (
            <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid={'related-case-' + idx}>
              <div>
                <label className="block text-xs text-white/40 mb-1">Case Number</label>
                <input
                  type="text"
                  value={rc.caseNumber}
                  onChange={function (e) { updateRelatedCase(idx, 'caseNumber', e.target.value); }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., 2024FL67890"
                  data-testid={'related-case-number-' + idx}
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">Case Type</label>
                <select
                  value={rc.caseType}
                  onChange={function (e) { updateRelatedCase(idx, 'caseType', e.target.value); }}
                  className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid={'related-case-type-' + idx}
                >
                  <option value="" className="bg-[#1C1F2E]">Select type</option>
                  <option value="Custody" className="bg-[#1C1F2E]">Custody</option>
                  <option value="Divorce" className="bg-[#1C1F2E]">Divorce</option>
                  <option value="Child Support" className="bg-[#1C1F2E]">Child Support</option>
                  <option value="Protective Order" className="bg-[#1C1F2E]">Protective Order</option>
                  <option value="Paternity" className="bg-[#1C1F2E]">Paternity</option>
                  <option value="Modification" className="bg-[#1C1F2E]">Modification</option>
                  <option value="Contempt" className="bg-[#1C1F2E]">Contempt</option>
                  <option value="Other" className="bg-[#1C1F2E]">Other</option>
                </select>
              </div>
              <button
                onClick={function () { removeRelatedCase(idx); }}
                className="p-2 text-white/20 hover:text-[#EF4444] transition-colors"
                data-testid={'remove-related-case-' + idx}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
